import { DateTime } from "luxon";
import { semvApi } from "../../../../../../shared/services/axios/apis.service";
import { GOVERNMENT_PLAN_STATUS } from "../../types/status";
import { GovPlanSecretaryProjectContract } from "../contracts/project";
import { GovPlanSecretaryProjectRoadmapItem } from "./roadmap-item";

export class GovPlanSecretaryProject {
    id: number;

    name: string;

    status: GOVERNMENT_PLAN_STATUS;

    description?: string;

    amount?: number;

    deadline: Date;

    observation?: string;

    completedAt?: Date;

    canceledAt?: Date;

    createdAt: Date;

    updatedAt: Date;

    roadmap: GovPlanSecretaryProjectRoadmapItem[];

    secretary: { id: number; name: string };

    constructor(contract: GovPlanSecretaryProjectContract) {
        this.id = contract.id;
        this.name = contract.name;
        this.status = contract.status;
        this.description = contract.description;
        this.amount = contract.amount;
        this.deadline = new Date(contract.deadline);
        this.observation = contract.observation;
        this.completedAt = contract.completedAt
            ? new Date(contract.completedAt)
            : undefined;
        this.canceledAt = contract.canceledAt
            ? new Date(contract.canceledAt)
            : undefined;
        this.createdAt = new Date(contract.createdAt);
        this.updatedAt = new Date(contract.updatedAt);
        this.roadmap = contract.roadmap
            .sort((a, b) => a.priority - b.priority)
            .map((it) => GovPlanSecretaryProjectRoadmapItem.fromContract(it));
        this.secretary = contract.secretary;
    }

    async reorderRoadmap(items: GovPlanSecretaryProjectRoadmapItem[]) {
        await semvApi.patch(
            `/government-plan/projects/${this.id}/items/reorder`,
            {
                itemsId: items.map((it) => it.id),
            }
        );
        this.roadmap = items;
        return this;
    }

    async update(payload: {
        name: string;
        description: string;
        amount: number;
        observation?: string;
        deadline: Date;
    }) {
        await semvApi.patch(`/government-plan/projects/${this.id}`, {
            ...payload,
            secretaryId: this.secretary.id,
        });
        return this;
    }

    get remainingBudget() {
        const used = this.roadmap.reduce(
            (prev, curr) => prev + (curr.amount || 0),
            0
        );
        return (this.amount || 0) - used;
    }

    get projectProgress() {
        const total = this.roadmap?.length;

        if (!total) return 0;
        const completed = this.roadmap.filter((it) => it.isCompleted()).length;

        return +((completed / total) * 100).toFixed(2);
    }

    isCompleted() {
        return [
            GOVERNMENT_PLAN_STATUS.COMPLETED,
            GOVERNMENT_PLAN_STATUS.COMPLETED_LATE,
        ].includes(this.status);
    }

    daysUntilDeadline() {
        const deadline = DateTime.fromJSDate(this.deadline);

        return Math.floor(deadline.diffNow("days").days);
    }

    nearDeadline() {
        const deadline = DateTime.fromJSDate(this.deadline);

        return (
            Math.floor(deadline.diffNow("days").days) <
            Math.floor(this.intervalBetweenCreationAndDeadline() / 2)
        );
    }

    isLate() {
        const deadline = DateTime.fromJSDate(this.deadline);

        return Math.floor(deadline.diffNow("days").days) < 0;
    }

    get variant(): "success" | "warning" | "danger" | "primary" {
        if (this.isCompleted()) return "success";

        if (this.isLate()) return "danger";

        if (this.nearDeadline()) return "warning";

        return "primary";
    }

    intervalBetweenCreationAndDeadline() {
        const createdAt = DateTime.fromJSDate(this.createdAt, {
            zone: "America/Sao_Paulo",
        }).startOf("day");
        const deadline = DateTime.fromJSDate(this.deadline, {
            zone: "America/Sao_Paulo",
        }).startOf("day");

        return deadline.diff(createdAt, "days").days;
    }
}
